import React, { useEffect } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import GridViewIcon from '@mui/icons-material/GridView';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Clock from '../../components/Clock';
import { useUser } from '../../contexts/UserContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { SHELL_DESTINATIONS, getDestinationLabel, useShellNav } from './ShellNavContext';
import LayoutToggle from './LayoutToggle';
import { shellSx, shellTokens } from './theme/shellTheme';
import TodayPage from './pages/TodayPage';
import StrategiesPage from './pages/StrategiesPage';
import TradePage from './pages/TradePage';
import PlatformPage from './pages/PlatformPage';
import MorePage from './pages/MorePage';
import ResearchPage from './pages/ResearchPage';
import AdminPage from './pages/AdminPage';
import StrategyDocsPage from '../docs/StrategyDocsPage';
import ContraLogo from '../../logo.svg';

const NAV_ICONS = {
    today: TodayIcon,
    strategies: GridViewIcon,
    trade: CurrencyRupeeIcon,
    platform: HubOutlinedIcon,
    more: MoreHorizIcon,
    research: ScienceOutlinedIcon,
    admin: SettingsOutlinedIcon,
    docs: DescriptionOutlinedIcon,
};

const PRIMARY_DESTINATIONS = SHELL_DESTINATIONS.filter((item) => item.mobileNav && !item.mobileOnly);
const EXTENDED_DESTINATIONS = SHELL_DESTINATIONS.filter((item) => item.desktopExtended);
const MOBILE_DESTINATIONS = SHELL_DESTINATIONS.filter((item) => item.mobileNav);

const renderDestination = (destination) => {
    switch (destination) {
        case 'today':
            return <TodayPage />;
        case 'strategies':
            return <StrategiesPage />;
        case 'trade':
            return <TradePage />;
        case 'platform':
            return <PlatformPage />;
        case 'more':
            return <MorePage />;
        case 'research':
            return <ResearchPage />;
        case 'admin':
            return <AdminPage />;
        case 'docs':
            return <StrategyDocsPage />;
        default:
            return <TodayPage />;
    }
};

const NavButton = ({ item, active, onClick, compact = false }) => {
    const Icon = NAV_ICONS[item.value] || TodayIcon;

    if (compact) {
        return (
            <Box
                component="button"
                type="button"
                onClick={() => onClick(item.value)}
                sx={{
                    flex: 1,
                    border: 'none',
                    bgcolor: 'transparent',
                    fontFamily: 'inherit',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    color: active ? shellTokens.primary : shellTokens.muted,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.4,
                    py: 0.5,
                }}
            >
                <Icon sx={{ fontSize: '1.15rem' }} />
                {item.label}
            </Box>
        );
    }

    return (
        <Box
            component="button"
            type="button"
            onClick={() => onClick(item.value)}
            sx={shellSx.navItem(active)}
        >
            <Icon sx={{ fontSize: '1rem', width: 20 }} />
            {item.label}
        </Box>
    );
};

const InsightsShellLayout = () => {
    const { user, logout } = useUser();
    const { mode, toggleTheme } = useThemeMode();
    const { destination, setDestination } = useShellNav();

    useEffect(() => {
        const previousBackground = document.body.style.backgroundImage;
        const previousBackgroundColor = document.body.style.backgroundColor;
        document.body.style.backgroundImage = 'none';
        document.body.style.backgroundColor = shellTokens.bg;

        return () => {
            document.body.style.backgroundImage = previousBackground;
            document.body.style.backgroundColor = previousBackgroundColor;
        };
    }, []);

    const handleLogout = () => {
        logout();
        window.location.href = '/access';
    };

    return (
        <Box sx={shellSx.app}>
            <Box component="aside" sx={shellSx.sidebar}>
                <Box sx={{ px: 2.25, py: 2, borderBottom: `1px solid ${shellTokens.border}` }}>
                    <Box component="img" src={ContraLogo} alt="Contra" sx={{ height: 28, mb: 0.5 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.03em' }}>
                        Contra Insights
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: shellTokens.primary,
                            mt: 0.25,
                        }}
                    >
                        Shell v2
                    </Typography>
                </Box>

                <Typography sx={shellSx.navGroupLabel}>Operations</Typography>
                {PRIMARY_DESTINATIONS.map((item) => (
                    <NavButton
                        key={item.value}
                        item={item}
                        active={destination === item.value}
                        onClick={setDestination}
                    />
                ))}

                <Typography sx={{ ...shellSx.navGroupLabel, display: { xs: 'none', md: 'block' } }}>
                    Extended
                </Typography>
                {EXTENDED_DESTINATIONS.map((item) => (
                    <Box key={item.value} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <NavButton
                            item={item}
                            active={destination === item.value}
                            onClick={setDestination}
                        />
                    </Box>
                ))}

                <Box sx={{ mt: 'auto', p: 2, borderTop: `1px solid ${shellTokens.border}`, color: shellTokens.muted }}>
                    <LayoutToggle compact sx={{ color: shellTokens.muted }} />
                </Box>
            </Box>

            <Box sx={shellSx.mainWrap}>
                <Box component="header" sx={shellSx.topbar}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>
                        {getDestinationLabel(destination)}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{ fontSize: '0.78rem', color: shellTokens.muted, fontFamily: shellTokens.monoFamily, display: { xs: 'none', sm: 'block' } }}
                    >
                        <Clock />
                    </Typography>
                    {user && (
                        <Typography sx={{ fontSize: '0.78rem', color: shellTokens.muted, display: { xs: 'none', md: 'block' } }}>
                            {user.full_name}
                        </Typography>
                    )}
                    <LayoutToggle compact sx={{ color: shellTokens.muted }} />
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleLogout}
                        sx={{
                            fontSize: '0.76rem',
                            borderColor: shellTokens.border,
                            color: shellTokens.text,
                            textTransform: 'none',
                            display: { xs: 'none', sm: 'inline-flex' },
                        }}
                    >
                        Logout
                    </Button>
                    <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} theme`}>
                        <IconButton size="small" onClick={toggleTheme} aria-label="Toggle theme">
                            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box component="main" sx={shellSx.content}>
                    {renderDestination(destination)}
                </Box>
            </Box>

            <Box component="nav" sx={shellSx.bottomNav}>
                <Box
                    sx={{
                        display: 'flex',
                        height: '100%',
                        maxWidth: 520,
                        mx: 'auto',
                        px: 0.5,
                    }}
                >
                    {MOBILE_DESTINATIONS.map((item) => (
                        <NavButton
                            key={item.value}
                            item={item}
                            active={destination === item.value}
                            onClick={setDestination}
                            compact
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default InsightsShellLayout;
